// src/Map/MapContainerWrapper.jsx
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, Circle, useMapEvents } from 'react-leaflet';
import { TbZoomReset } from "react-icons/tb";
import { MdMyLocation } from "react-icons/md";
import DropdownLayerSelector from './LayerSelector/LayerSelector';
import Measurment from './measurment/Measurment';
import ClickHandler from "../ClickHandler";
import GeoJsonControls from '../Controls';
import TiffProcessor from '../../maps/TiffProcessor';
import useMapStore from '../useMapStore';

const MapClickHandler = ({ setPoints, calculateDistance, setDistance }) => {
    useMapEvents({
        click: (e) => {
            const { lat, lng } = e.latlng;
            setPoints((prevPoints) => {
                if (prevPoints.length === 2) {
                    return [{ lat, lng }];
                }
                const newPoints = [...prevPoints, { lat, lng }];
                if (newPoints.length === 2) {
                    const dist = calculateDistance(
                        newPoints[0].lat,
                        newPoints[0].lng,
                        newPoints[1].lat,
                        newPoints[1].lng
                    );
                    setDistance(dist); // ✅ مهم
                }
                return newPoints;
            });
        }
    });
    return null;
};


const MapContainerWrapper = ({
    zoomLevel,
    setZoomLevel,
    selectedLayer,
    handleLayerChange,
    baseLayers,
    mapRef,
    setMapRef,
    isMapClickable,
    toggleMapClick,
    isClicked,
    handleClick,
    showTiff,
    tiffType,
    startDate,
    pointData,
    pointData1,
    pointData2,
    pointToLayer,
    pointToLayer2,
    pointToLayer3,
    onEachFeature,
    currentMarker,
    circleCenter,
    markerPosition,
    setMarkerPosition,
    isClickActive,
    handleButtonClick,
    points,
    setPoints,
    distance,
    calculateDistance,
    mapText,
    setDistance,
    isAdmin,
    province, provinceCenters
}) => {
    const [hasZoomedToProvince, setHasZoomedToProvince] = useState(false);
    const [map, setMap] = useState(null);

    // useMapStore is module-scoped, so whatever is left in it outlives this
    // component. react-leaflet calls map.remove() when MapContainer unmounts,
    // and a removed map keeps _loaded === true while its panes are gone -- so
    // setView() on it walks into _tryAnimatedPan and throws. Leaving the dead
    // instance behind meant the next mount read it and crashed the whole app
    // (go to /admin, come back to /, blank page).
    useEffect(() => {
        if (!map) return undefined;
        setMapRef(map);
        // Only give up the slot if nothing newer has claimed it, so an overlapping
        // mount can't be undone by the outgoing one's cleanup.
        return () => {
            if (useMapStore.getState().mapRef === map) setMapRef(null);
        };
    }, [map, setMapRef]);

    useEffect(() => {
        if (!mapRef) return;
        const handleZoom = () => {
            const newZoom = mapRef.getZoom();
            setZoomLevel(newZoom);
        };
        mapRef.on('zoomend', handleZoom);
        return () => mapRef.off('zoomend', handleZoom);
    }, [mapRef]);

    useEffect(() => {
        if (!mapRef || hasZoomedToProvince) return;

        if (isAdmin) {
            mapRef.setView([32.4279, 53.6880], 6);
            setHasZoomedToProvince(true);
        } else if (provinceCenters?.[province]) {
            const [lat, lng] = provinceCenters[province];
            mapRef.setView([lat, lng], 8);
            setHasZoomedToProvince(true);
        }
    }, [mapRef, province, isAdmin, provinceCenters, hasZoomedToProvince]);


    return (
        <div className="map">
         
            <MapContainer
                center={[32.4279, 53.6880]}
                zoom={zoomLevel}
                className=" h-full w-full rounded-lg shadow-lg"
                whenReady={({ target }) => setMap(target)}
            >
                <TileLayer
                    key={selectedLayer}
                    url={baseLayers[selectedLayer].url}
                    attribution={baseLayers[selectedLayer].attribution}
                />
                {showTiff && mapRef?.current && (
                    <TiffProcessor
                        map={mapRef.current}
                        startDate={startDate}
                        type={tiffType}
                    />
                )}


                {pointData && pointData1 && pointData2 && (
                    <GeoJsonControls
                        key={zoomLevel}
                        pointData={pointData}
                        pointData1={pointData1}
                        pointData2={pointData2}
                        pointToLayer={pointToLayer}
                        pointToLayer2={pointToLayer2}
                        pointToLayer3={pointToLayer3}
                        onEachFeature={onEachFeature}
                    />
                )}


                <div className='flex flex-col absolute left-2 bottom-2 gap-y-2 z-[100000]'>
                    <div
                        className={`cursor-pointer w-fit lg:w-[11rem] ${isClicked ? "bg-[#f39c12]" : "bg-[#00000080]"
                            } text-white rounded-t-0 rounded-l-[2em] rounded-r-[2em] items-center py-1 justify-between px-1 rounded-b-0 flex relative clear-both`}
                        onClick={handleClick}
                    >
                        <p className="text-[16px] hidden lg:flex font-[myFont] px-1">بزرگ‌نمایی پیش‌فرض</p>
                        <div className={`shadow-lg p-2 ${isClicked ? "bg-[#f39c12]" : "bg-white"} w-fit rounded-full text-black`}>
                            <TbZoomReset color={isClicked ? "#ffffff" : "#0272b2"} size={20} />
                        </div>
                    </div>

                    <div
                        className={`w-fit lg:w-[11rem] cursor-pointer ${isMapClickable ? "bg-[#f39c12]" : "bg-[#00000080]"
                            } text-white rounded-t-0 rounded-l-[2em] rounded-r-[2em] items-center py-1 justify-between px-1 rounded-b-0 flex relative clear-both`}
                        onClick={toggleMapClick}
                    >
                        <p className="text-[16px] hidden lg:flex font-[myFont] px-1">
                            {isMapClickable ? "غیرفعال کردن" : "مختصات‌یاب"}
                        </p>
                        <div className={`p-2 ${isMapClickable ? "bg-[#f39c12]" : "bg-white"} w-fit rounded-full text-black`}>
                            <MdMyLocation color={isMapClickable ? "#ffffff" : "#0272b2"} size={20} />
                        </div>
                    </div>

                    <DropdownLayerSelector
                        selectedLayer={selectedLayer}
                        onLayerChange={handleLayerChange}
                    />
                    <Measurment isClickActive={isClickActive} handleButtonClick={handleButtonClick} />
                </div>

                {/* TIFF */}
                {showTiff && <TiffProcessor map={mapRef} startDate={startDate} type={tiffType} />}

                {/* Marker Click */}
                {isMapClickable && (
                    <ClickHandler onClick={setMarkerPosition} isMapClickable={isMapClickable} />
                )}
                {markerPosition && isMapClickable && (
                    <Popup position={[markerPosition.lat, markerPosition.lng]}>
                        📍 مختصات: <br />
                        lat: {markerPosition.lat.toFixed(6)} <br />
                        lon: {markerPosition.lng.toFixed(6)}
                    </Popup>
                )}

                {/* اندازه‌گیری فاصله */}
                {isClickActive && (
                    <>
                        <MapClickHandler setPoints={setPoints} calculateDistance={calculateDistance} setDistance={setDistance}
                        />
                        {points[0] && (
                            <CircleMarker center={[points[0].lat, points[0].lng]} radius={3} color="black" />
                        )}
                        {points[1] && (
                            <CircleMarker center={[points[1].lat, points[1].lng]} radius={3} color="black" />
                        )}
                        {points.length === 2 && (
                            <Polyline
                                positions={[[points[0].lat, points[0].lng], [points[1].lat, points[1].lng]]}
                                color="black"
                            />
                        )}
                    </>
                )}

                {currentMarker && (
                    <CircleMarker
                        center={[currentMarker.lat, currentMarker.lng]}
                        radius={8}
                        color="#00852D"
                        fillColor="#00852D"
                        fillOpacity={1}
                    >
                        <Popup>
                            {mapText.latAndLonText}: [{currentMarker.lat}, {currentMarker.lng}]
                        </Popup>
                    </CircleMarker>
                )}

                {circleCenter && (
                    <Circle
                        center={[circleCenter.lat, circleCenter.lng]}
                        radius={500}
                        color="blue"
                        fillColor="blue"
                        fillOpacity={0.3}
                    />
                )}
            </MapContainer>
        </div>
    );
};

export default MapContainerWrapper;
